import React, { useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import "../../../assets/styles/onlyinvoiceprint.css";
import useGetOneConsignmentInvoice from "@hooks/consignment/useGetOneConsignmentInvoice";

const formatCurrency = (amount: any) => {
  if (amount === null || amount === undefined) return "0";
  return Number(amount).toLocaleString("en-US");
};

const ConsignmentInvoicePrintPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { consignmentInvoiceData } = useGetOneConsignmentInvoice(id!);

  const retrieveValues = {
    invoice_date: consignmentInvoiceData?.invoice_date,
    SSIC: consignmentInvoiceData?.consignment_invoice_code,
    SSOC: consignmentInvoiceData?.consignment_code,
    sales_person_name: consignmentInvoiceData?.sales_person_name,
    delivery_date: consignmentInvoiceData?.delivery_date,
    business_unit_name: consignmentInvoiceData?.business_unit_name,
    customer_phone: consignmentInvoiceData?.cus_phone,
    address: consignmentInvoiceData?.address,
    contact_name: consignmentInvoiceData?.contact_name,
    created_time: consignmentInvoiceData?.created_time,
    invoice_person_name:
      consignmentInvoiceData?.invoice_person_name ||
      consignmentInvoiceData?.created_by_name ||
      consignmentInvoiceData?.sales_person_name,
    invoice_user_role_name:
      consignmentInvoiceData?.invoice_user_role_name ||
      consignmentInvoiceData?.user_role_name ||
      "",
    customer_address: consignmentInvoiceData?.customer_address,
    sub_total: consignmentInvoiceData?.sub_total,
    discount_amount: consignmentInvoiceData?.discount_amount,
    tax_amount: consignmentInvoiceData?.tax_amount,
    other_charges: consignmentInvoiceData?.other_charges,
    grand_total_amount: consignmentInvoiceData?.grand_total_amount,
    payment_type_name: consignmentInvoiceData?.payment_type_name,
    item_detail: consignmentInvoiceData?.products,
  };

  //@ts-ignore
  const chunkArray = (arr, size) => {
    const chunks = [];
    for (let i = 0; i < arr.length; i += size) {
      chunks.push(arr.slice(i, i + size));
    }
    return chunks;
  };

  const chunkedItems = chunkArray(retrieveValues?.item_detail || [], 3);

  useEffect(() => {
    const handleAfterPrint = () => navigate(-1);
    window.addEventListener("afterprint", handleAfterPrint);
    return () => window.removeEventListener("afterprint", handleAfterPrint);
  }, [navigate]);

  useEffect(() => {
    const timeout = window.setTimeout(() => window.print(), 300);
    return () => window.clearTimeout(timeout);
  }, []);

  const getTimeFormat = (date: any): string => {
    if (!date) return "";
    const reminderTime = new Date(date);
    return reminderTime.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  };

  const maxRows = 0;
  const dataRowsCount = (retrieveValues.item_detail || []).length;
  const blankRowsCount = Math.max(0, maxRows - dataRowsCount);
  const blankRows = Array.from({ length: blankRowsCount });

  if (!retrieveValues.SSIC) {
    return (
      <div className="p-10 font-sans text-lg text-center">
        Loading Invoice Data...
      </div>
    );
  }

  return (
    <div className="bg-white print-h-auto min-h-screen font-sans text-gray-800 text-xs leading-normal print-page">
      <div className="mx-auto p-8 w-full max-w-4xl print-container">
        <div className="mb-4 text-left header">
          <h1
            className="mb-2 font-bold text-[18px] text-xl"
            style={{ fontSize: 22 }}
          >
            STL
          </h1>
        </div>

        <div className="mb-2 border-black border-b w-full"></div>
        <br />
        <div className="mb-2 border-black border-b w-full"></div>

        <div className="flex justify-end mb-4">
          <h1 className="font-bold text-xl uppercase">INVOICE</h1>
        </div>

        <div className="flex justify-between mb-8">
          <div className="pr-4 w-1/2">
            <h2 className="mb-1 font-bold">Bill To:</h2>
            <p className="font-bold">{retrieveValues.business_unit_name}</p>
            <p>{retrieveValues.address}</p>
            <p>Phone: {retrieveValues.customer_phone}</p>
            <p>Attention: {retrieveValues.contact_name}</p>
            <p>{retrieveValues.customer_address}</p>
          </div>

          <div className="flex justify-end w-1/2">
            <table className="text-left whitespace-nowrap table-auto">
              <tbody>
                <tr>
                  <td className="pr-4 border-none font-bold">Invoice No</td>
                  <td className="pr-2">:</td>
                  <td>{retrieveValues.SSIC}</td>
                </tr>
                <tr>
                  <td className="pr-4 font-bold">Date</td>
                  <td className="pr-2">:</td>
                  <td>
                    {retrieveValues?.invoice_date?.split("T")[0] || "-"}{" "}
                    {getTimeFormat(retrieveValues?.created_time)}
                  </td>
                </tr>
                <tr>
                  <td className="pr-4 font-bold">S.O Number</td>
                  <td className="pr-2">:</td>
                  <td>{retrieveValues.SSOC}</td>
                </tr>
                <tr>
                  <td className="pr-4 font-bold">Sales Person</td>
                  <td className="pr-2">:</td>
                  <td>{retrieveValues.sales_person_name}</td>
                </tr>
                <tr>
                  <td className="pr-4 font-bold">Delivery Date</td>
                  <td className="pr-2">:</td>
                  <td>{retrieveValues?.delivery_date?.split("T")[0] || "-"}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <table className="w-full border-collapse">
          <thead>
            <tr className="bg-gray-100 border border-gray-400">
              <th className="p-1 border border-gray-400 w-1/12 font-bold text-center">
                ITEM NO
              </th>
              <th className="p-1 border border-gray-400 w-4/12 font-bold text-left">
                DESCRIPTION
              </th>
              <th className="p-1 border border-gray-400 w-[8%] font-bold text-center">
                QTY
              </th>
              <th className="p-1 border border-gray-400 w-1/6 font-bold text-right">
                PRICE
              </th>
              <th className="p-1 border border-gray-400 w-[10%] font-bold text-center">
                UNIT
              </th>
              <th className="p-1 border border-gray-400 w-1/6 font-bold text-right">
                AMOUNT
              </th>
            </tr>
          </thead>
          <tbody>
            {
              //@ts-ignore
              (retrieveValues.item_detail || []).map((item, index) => (
                <tr key={index} className="h-6">
                  <td className="p-1 border border-gray-400 text-center">
                    {item.product_code}
                  </td>
                  <td className="p-1 border border-gray-400 text-left">
                    {item.item_description || item.description || item.product_name}
                  </td>
                  <td className="p-1 border border-gray-400 text-center">
                    {item.qty}
                  </td>
                  <td className="p-1 border border-gray-400 text-right">
                    {formatCurrency(item.price ?? item.sales_price)}
                  </td>
                  <td className="p-1 border border-gray-400 text-center">
                    {item.unit_name}
                  </td>
                  <td className="p-1 border border-gray-400 text-right">
                    {formatCurrency(item.amount)}
                  </td>
                </tr>
              ))
            }

            {blankRows.map((_, index) => (
              <tr key={`blank-${index}`} className="h-6">
                <td className="p-1 border border-gray-400"></td>
                <td className="p-1 border border-gray-400"></td>
                <td className="p-1 border border-gray-400"></td>
                <td className="p-1 border border-gray-400"></td>
                <td className="p-1 border border-gray-400"></td>
                <td className="p-1 border border-gray-400"></td>
              </tr>
            ))}

            <tr className="h-6">
              <td
                colSpan={5}
                className="p-1 border border-gray-400 font-bold text-right"
              >
                Sub Total
              </td>
              <td className="p-1 border border-gray-400 text-right">
                {formatCurrency(retrieveValues.sub_total)}
              </td>
            </tr>
            <tr className="h-6">
              <td
                colSpan={5}
                className="p-1 border border-gray-400 font-bold text-right"
              >
                Discount
              </td>
              <td className="p-1 border border-gray-400 text-right">
                {formatCurrency(retrieveValues.discount_amount)}
              </td>
            </tr>
            <tr className="h-6">
              <td
                colSpan={5}
                className="p-1 border border-gray-400 font-bold text-right"
              >
                Tax
              </td>
              <td className="p-1 border border-gray-400 text-right">
                {formatCurrency(retrieveValues.tax_amount)}
              </td>
            </tr>
            <tr className="h-6">
              <td
                colSpan={5}
                className="p-1 border border-gray-400 font-bold text-right"
              >
                Other Charges
              </td>
              <td className="p-1 border border-gray-400 text-right">
                {formatCurrency(retrieveValues.other_charges)}
              </td>
            </tr>
            <tr className="h-6">
              <td
                colSpan={5}
                className="bg-gray-200 p-1 border border-gray-400 font-bold text-right"
              >
                Grand Total
              </td>
              <td className="bg-gray-200 p-1 border border-gray-400 font-bold text-right">
                {formatCurrency(retrieveValues.grand_total_amount)}
              </td>
            </tr>
          </tbody>
        </table>

        <div className="my-4">
          <p className="font-bold">
            Note: Please make sure receiving official Cash Receipt after each
            payment.
          </p>
        </div>

        <div className="flex justify-end mt-12">
          <div className="space-y-1 text-center">
            <h3 className="mb-1 font-bold text-sm">AUTHORIZED BY</h3>
            <div className="mt-8">
              <p className="font-bold">{retrieveValues?.invoice_person_name}</p>
              <p className="text-gray-600">
                {retrieveValues?.invoice_user_role_name}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConsignmentInvoicePrintPage;
